import LoadingSpinner from '@common/src/components/LoadingSpinner';
import useQueryParams from '@common/src/hooks/useQueryParams';
import {
  useAnvaFlowsCoverages,
  flattenCoverageCodes,
} from '@flows/src/api/anva-flows/anva-config/getAnvaFlowsCoverages';
import FlowsConfigureCoverageItem from '@flows/src/components/flows-configure/insurances/FlowsConfigureCoverageItem';
import ConfigureItemsGrid from '@flows/src/components/flows-configure/shared/ConfigureItemsGrid';
import ContentWrapper from '@flows/src/components/flows-configure/shared/ContentWrapper';
import SelectAllButton from '@flows/src/components/flows-configure/shared/SelectAllButton';
import { FLOWS_CONFIG_PATH } from '@flows/src/FlowsConfigRouter';
import { useFlowsConfigureParams } from '@flows/src/hooks/flows-configure/useFlowsConfigureParams';
import useFlowsConfigureSelectAllButtonProps from '@flows/src/hooks/flows-configure/useFlowsConfigureSelectAllButtonProps';
import { useAnvaFlowsConfigureStore } from '@flows/src/store/zustand/flows-configure/anvaFlowsConfigureStore';
import { PartyType } from '@parties/src/types';
import { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Redirect, useHistory } from 'react-router';

const FlowsConfigureAnvaCoverages: FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const queryparams = useQueryParams();
  const partyType = (queryparams.get('type') as PartyType) || 'CUSTOMER';
  const { flowId, insuranceId: insuranceGroupId } = useFlowsConfigureParams();
  const insuranceGroups = useAnvaFlowsConfigureStore(
    (state) => state.insurances,
  );
  const [error, setError] = useState<string | undefined>(undefined);
  const hasAutoSelectedRef = useRef<Record<string, boolean>>({});
  const insuranceGroup = insuranceGroups.find(
    (group) => group.id === Number(insuranceGroupId),
  );
  const { data: groupedCoveragesData, isLoading } = useAnvaFlowsCoverages(
    partyType,
    insuranceGroup ? insuranceGroup?.insurance_ids : undefined,
  );

  // Extract grouped coverages and flatten coverage codes for store operations
  const groupedCoverages = groupedCoveragesData?.items || [];
  const allCoverageCodes = flattenCoverageCodes(groupedCoverages);

  const { handleDeselectAll, handleSelectAll } =
    useFlowsConfigureSelectAllButtonProps('coverages', insuranceGroupId);

  // Keep track of navigation direction
  const isNavigatingBack = useRef(false);
  useEffect(() => {
    const unblock = history.block((location, action) => {
      isNavigatingBack.current = action === 'POP';
    });
    return () => unblock();
  }, [history]);

  useEffect(() => {
    if (
      !isLoading &&
      groupedCoverages.length &&
      insuranceGroupId &&
      !isNavigatingBack.current &&
      !hasAutoSelectedRef.current[insuranceGroupId]
    ) {
      const existingSelections =
        useAnvaFlowsConfigureStore.getState().coverages[insuranceGroupId];

      // Auto-select mandatory coverages first
      const mandatoryCoverageCodes = groupedCoverages
        .filter((group) => group.mandatory)
        .reduce((acc: string[], group) => acc.concat(group.coverage_codes), []);

      // Only auto-select if no coverages are currently selected
      if (!existingSelections || existingSelections.length === 0) {
        // Auto-select all coverage codes from all groups by default
        handleSelectAll(allCoverageCodes);
        hasAutoSelectedRef.current[insuranceGroupId] = true;
      } else {
        // Ensure mandatory coverages are always selected, even if user had previous selections
        const updatedSelections = [...existingSelections];
        let hasNewMandatorySelections = false;

        mandatoryCoverageCodes.forEach((code) => {
          if (!updatedSelections.includes(code)) {
            updatedSelections.push(code);
            hasNewMandatorySelections = true;
          }
        });

        if (hasNewMandatorySelections) {
          useAnvaFlowsConfigureStore
            .getState()
            .setCoverages(insuranceGroupId, updatedSelections);
        }

        // Mark as auto-selected if there are already selections
        hasAutoSelectedRef.current[insuranceGroupId] = true;
      }

      // If there's only one group with one coverage code, auto-navigate to next step
      if (groupedCoverages.length === 1 && allCoverageCodes.length === 1) {
        // Find the next insurance that needs coverage selection
        const currentIndex = insuranceGroups.findIndex(
          (group) => group.id === Number(insuranceGroupId),
        );

        const nextInsuranceIndex = currentIndex + 1;
        let foundNextCoverage = false;

        // Look for the next insurance that has multiple coverages
        while (nextInsuranceIndex < insuranceGroups.length) {
          const nextInsurance = insuranceGroups[nextInsuranceIndex];
          // We can't check coverage count here, so we'll navigate to the next insurance's coverage page
          // and let it handle the logic
          history.push({
            pathname: `${FLOWS_CONFIG_PATH}/${flowId}/insurances/${nextInsurance.id}/coverages`,
            search: window.location.search,
          });
          foundNextCoverage = true;
          break;
        }

        if (!foundNextCoverage) {
          // No more insurances need coverage selection, move to companies phase
          // Start with the first insurance's companies
          const firstInsurance = insuranceGroups[0];
          history.push({
            pathname: `${FLOWS_CONFIG_PATH}/${flowId}/insurances/${firstInsurance.id}/companies`,
            search: window.location.search,
          });
        }
      }
    }
  }, [
    isLoading,
    groupedCoverages,
    allCoverageCodes,
    insuranceGroupId,
    flowId,
    history,
    handleSelectAll,
    insuranceGroups,
  ]);

  const isAllSelected = useAnvaFlowsConfigureStore(
    (state) =>
      state.coverages[insuranceGroupId || '']?.length ===
      allCoverageCodes.length,
  );

  if (
    !insuranceGroupId ||
    !insuranceGroups.map(({ id }) => id).includes(Number(insuranceGroupId))
  ) {
    return (
      <Redirect
        to={{
          pathname: `${FLOWS_CONFIG_PATH}/${flowId}/insurances`,
          search: window.location.search,
        }}
      />
    );
  }

  const handlePrevious = () => {
    // When going back from coverages, we need to find the previous insurance with multiple coverages
    const currentIndex = insuranceGroups.findIndex(
      (group) => group.id === Number(insuranceGroupId),
    );

    if (
      currentIndex === 0 ||
      (groupedCoverages.length === 1 && allCoverageCodes.length === 1)
    ) {
      // If this is the first insurance or has only one coverage, go back to insurance selection
      history.push({
        pathname: `${FLOWS_CONFIG_PATH}/${flowId}/insurances`,
        search: window.location.search,
      });
    } else {
      // Go to the previous insurance's coverages
      const prevInsurance = insuranceGroups[currentIndex - 1];
      history.push({
        pathname: `${FLOWS_CONFIG_PATH}/${flowId}/insurances/${prevInsurance.id}/coverages`,
        search: window.location.search,
      });
    }
  };

  const handleNext = () => {
    const currentCoverages =
      useAnvaFlowsConfigureStore.getState().coverages?.[insuranceGroupId] || [];

    // Check if any coverages are selected OR if there are mandatory coverages that should be auto-selected
    const mandatoryCoverageCodes = groupedCoverages
      .filter((group) => group.mandatory)
      .reduce((acc: string[], group) => acc.concat(group.coverage_codes), []);

    const areCoveragesSelected =
      currentCoverages.length > 0 || mandatoryCoverageCodes.length > 0;

    // If there are mandatory coverages but nothing selected, auto-select them now
    if (currentCoverages.length === 0 && mandatoryCoverageCodes.length > 0) {
      useAnvaFlowsConfigureStore
        .getState()
        .setCoverages(insuranceGroupId, mandatoryCoverageCodes);
    }

    if (!areCoveragesSelected) {
      setError(t('Select at least one option'));
      return;
    }

    // Find the next insurance that needs coverage selection
    const currentIndex = insuranceGroups.findIndex(
      (group) => group.id === Number(insuranceGroupId),
    );

    const nextInsuranceIndex = currentIndex + 1;

    if (nextInsuranceIndex < insuranceGroups.length) {
      // Go to the next insurance's coverage page
      const nextInsurance = insuranceGroups[nextInsuranceIndex];
      history.push({
        pathname: `${FLOWS_CONFIG_PATH}/${flowId}/insurances/${nextInsurance.id}/coverages`,
        search: window.location.search,
      });
    } else {
      // No more insurances need coverage selection, move to companies phase
      // Start with the first insurance's companies
      const firstInsurance = insuranceGroups[0];
      history.push({
        pathname: `${FLOWS_CONFIG_PATH}/${flowId}/insurances/${firstInsurance.id}/companies`,
        search: window.location.search,
      });
    }
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <ContentWrapper
      onNext={handleNext}
      onPrevious={handlePrevious}
      question={t('Guarantees for', { company: insuranceGroup?.name })}
      error={error}
    >
      <ConfigureItemsGrid>
        {groupedCoverages && groupedCoverages.length > 1 && (
          <SelectAllButton
            isAllSelected={isAllSelected}
            onDeselectAll={handleDeselectAll}
            onSelectAll={() => handleSelectAll(allCoverageCodes)}
          />
        )}
        {groupedCoverages.map((groupedCoverage) => (
          <FlowsConfigureCoverageItem
            key={groupedCoverage.adn_code}
            insuranceId={insuranceGroupId}
            isError={!!error}
            groupedCoverage={groupedCoverage}
            clearError={() => setError(undefined)}
          />
        ))}
      </ConfigureItemsGrid>
    </ContentWrapper>
  );
};

export default FlowsConfigureAnvaCoverages;
